import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Pagination,
} from "@mui/material";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DashboardCard from "./DashboardCard";
import AddArchitecturalRequest from "./AddArchitecturalRequest";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import api, { API_BASE_URL } from "../../utils/api";

const ITEMS_PER_PAGE = 5;

const ArchitecturalRequests = ({ propertyId }) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [allModalOpen, setAllModalOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const handleOpenCreate = () => setCreateModalOpen(true);
  const handleCloseCreate = () => setCreateModalOpen(false);
  const handleOpenAll = () => setAllModalOpen(true);
  const handleCloseAll = () => {
    setAllModalOpen(false);
    setPage(1);
  };

  const approved = requests.filter((r) => r.status?.toLowerCase() === "approved").length;
  const paginatedRequests = requests.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  useEffect(() => {
    const fetchRequests = async () => {
      if (!propertyId) return;
      try {
        const res = await api.get(`/architectural-requests`);
        const filtered = res.data.filter(
          (r) => r.property_id?.toLowerCase() === propertyId.toLowerCase()
        );
        setRequests(filtered);
      } catch (err) {
        console.error("Error fetching architectural requests:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [propertyId]);

  return (
    <DashboardCard title="Architectural Requests">
      {/* Create Request Modal */}
      <Modal open={createModalOpen} onClose={handleCloseCreate}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <AddArchitecturalRequest
            propertyId={propertyId}
            onClose={handleCloseCreate}
          />
        </Box>
      </Modal>

      {/* Summary Section */}
      <Box sx={{ mt: 4 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography>Total Requests:</Typography>
                <Typography variant="h4" color="primary">
                  {requests.length}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>Approved:</Typography>
                <Typography variant="h4" color="success.main">
                  {approved}
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {requests.slice(0, 2).map((req) => (
                <Grid item xs={12} md={6} key={req.id}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                  >
                    <Typography><strong>ID:</strong> {req.id}</Typography>
                    <Typography><strong>Type:</strong> {req.request_type || "N/A"}</Typography>
                    <Typography><strong>Status:</strong> {req.status}</Typography>
                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/home/memberspage/ArchitecturalRequests/${req.id}`)}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Spacing before action buttons */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreate}
              >
                Create Request
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<HomeRepairServiceIcon />}
                onClick={handleOpenAll}
              >
                View All Requests
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* View All Modal */}
      <Dialog open={allModalOpen} onClose={handleCloseAll} maxWidth="md" fullWidth>
        <DialogTitle>All Architectural Requests</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {paginatedRequests.map((req) => (
              <Grid item xs={12} key={req.id}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography><strong>ID:</strong> {req.id}</Typography>
                  <Typography><strong>Type:</strong> {req.request_type || "N/A"}</Typography>
                  <Typography><strong>Status:</strong> {req.status}</Typography>
                  <Typography><strong>Description:</strong> {req.description || "No description"}</Typography>
                  <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/home/memberspage/ArchitecturalRequests/${req.id}`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 1 }}>
          <Pagination
            count={Math.ceil(requests.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
          <Button onClick={handleCloseAll}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardCard>
  );
};

export default ArchitecturalRequests;
